import React, { useEffect, useState } from "react";
import { FlaskConical, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { stratToday, stratTrials, stratRun } from "../lib/api";

const tone = (s) => (s === "won" ? "text-emerald-300" : s === "lost" ? "text-rose-300" : "text-cyan-200/60");

const TrialRow = ({ t }) => (
  <tr className="border-t border-white/5" data-testid={`strat-trial-${t.id}`}>
    <td className="py-1 pr-2 text-cyan-50 truncate max-w-[180px]">{t.name}{t.candidate && <span className="ml-1 text-[9px] text-amber-300">CANDIDATE</span>}</td>
    <td className="py-1 pr-2 text-cyan-200/60">{t.author === "mr_strat" ? "Mr Strat" : "Nexus"}</td>
    <td className={`py-1 pr-2 capitalize ${tone(t.status)}`}>{t.status}</td>
    <td className={`py-1 text-right tabular-nums ${tone(t.status)}`}>{t.pnl_pct ?? t.last_pnl_pct ?? "—"}{(t.pnl_pct ?? t.last_pnl_pct) != null ? "%" : ""}</td>
  </tr>
);

export const StrategyLabCard = () => {
  const [today, setToday] = useState(null);
  const [trials, setTrials] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([stratToday(), stratTrials()]).then(([a, b]) => { setToday(a); setTrials(b.items || []); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try { await stratRun(); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Strategy run failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#050b14] border border-cyan-500/30 rounded-2xl p-4 space-y-3" data-testid="strategy-lab-card">
      <div className="flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-[#00F0FF]" />
        <p className="text-cyan-100 font-bold">Strategy Lab</p>
        <span className="text-[11px] text-cyan-200/50 truncate">Mr Strat + Hercules 2.0 · daily 5am CT · paper only, $100 cap</span>
        <button onClick={run} disabled={busy} data-testid="strategy-lab-run" className="ml-auto text-xs text-cyan-300 border border-cyan-500/40 rounded-full px-3 py-1 flex items-center gap-1.5 hover:bg-cyan-500/10 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run now
        </button>
      </div>
      {(today?.items || []).length === 0 ? <p className="text-xs text-cyan-100/50" data-testid="strategy-lab-empty">No strategies yet — tap Run now.</p> : today.items.map((s) => (
        <div key={s.id} className="rounded-xl px-3 py-2 border border-white/10 bg-white/[0.02]" data-testid={`strategy-today-${s.author}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black tracking-widest text-[#030712] bg-[#00F0FF] rounded px-1.5 py-0.5">{s.author === "mr_strat" ? "MR STRAT" : "NEXUS"}</span>
            <span className="font-bold text-cyan-50">{s.name}</span>
            <span className="ml-auto text-[11px] text-cyan-100/70 tabular-nums">{s.symbol} {s.direction} · {s.horizon_hours}h · SL {s.stop_loss}% · TP {s.take_profit}%</span>
          </div>
          <p className="text-xs text-cyan-100/80 mt-1">{s.thesis}</p>
        </div>
      ))}
      <table className="w-full text-xs" data-testid="strategy-trials-table">
        <thead><tr className="text-cyan-300/50 text-left"><th className="font-normal">Strategy</th><th className="font-normal">Author</th><th className="font-normal">Status</th><th className="font-normal text-right">P&L</th></tr></thead>
        <tbody>{trials.map((t) => <TrialRow key={t.id} t={t} />)}</tbody>
      </table>
    </div>
  );
};
